import { useCallback, useLayoutEffect, useRef, useState } from "react"

import { cn } from "@coval/ui/cn"

import { type SetSlice } from "./UploadedConversationsVisualizerPieBackground"

const LEGEND_ACTIVE_ROW_EDGE_SAFETY_PX = 28

const DEFAULT_LEGEND_OVERFLOW_STATE = {
  canScrollDown: false,
  canScrollUp: false,
  hasOverflow: false,
}

interface UploadedConversationsVisualizerLegendReelProps {
  activeBucketKey: string | null
  onBucketClick?: (bucketKey: string) => void
  onBucketHoverChange?: (bucketKey: string | null) => void
  sliceMarkPalette: string[]
  slices: SetSlice[]
}

export function UploadedConversationsVisualizerLegendReel({
  activeBucketKey,
  onBucketClick,
  onBucketHoverChange,
  sliceMarkPalette,
  slices,
}: UploadedConversationsVisualizerLegendReelProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const isPointerOverLegendRef = useRef(false)
  const legendListRef = useRef<HTMLUListElement>(null)
  const legendRowRefs = useRef<Record<string, HTMLLIElement | null>>({})
  const [legendOverflowState, setLegendOverflowState] = useState(
    DEFAULT_LEGEND_OVERFLOW_STATE,
  )

  const syncLegendOverflowState = useCallback(() => {
    const viewport = viewportRef.current

    if (!viewport) {
      return
    }

    const maxScrollTop = Math.max(
      0,
      viewport.scrollHeight - viewport.clientHeight,
    )
    const hasOverflow = maxScrollTop > 1
    const canScrollUp = hasOverflow && viewport.scrollTop > 1
    const canScrollDown = hasOverflow && viewport.scrollTop < maxScrollTop - 1

    setLegendOverflowState((currentState) => {
      if (
        currentState.hasOverflow === hasOverflow &&
        currentState.canScrollUp === canScrollUp &&
        currentState.canScrollDown === canScrollDown
      ) {
        return currentState
      }

      return {
        canScrollDown,
        canScrollUp,
        hasOverflow,
      }
    })
  }, [])

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const legendList = legendListRef.current

    if (!viewport || !legendList) {
      return
    }

    let animationFrame = 0

    const queueSync = () => {
      window.cancelAnimationFrame(animationFrame)
      animationFrame = window.requestAnimationFrame(syncLegendOverflowState)
    }

    queueSync()

    viewport.addEventListener("scroll", queueSync)

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            queueSync()
          })

    resizeObserver?.observe(viewport)
    resizeObserver?.observe(legendList)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      viewport.removeEventListener("scroll", queueSync)
      resizeObserver?.disconnect()
    }
  }, [syncLegendOverflowState, slices])

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const legendList = legendListRef.current

    if (!viewport || !legendList) {
      return
    }

    if (!activeBucketKey) {
      syncLegendOverflowState()
      return
    }

    if (isPointerOverLegendRef.current) {
      syncLegendOverflowState()
      return
    }

    const activeLegendRow = legendRowRefs.current[activeBucketKey]
    if (!activeLegendRow) {
      syncLegendOverflowState()
      return
    }

    let animationFrame = 0

    const measureAndAdjust = () => {
      const viewportHeight = viewport.clientHeight
      const maxScrollTop = Math.max(0, viewport.scrollHeight - viewportHeight)
      const currentScrollTop = viewport.scrollTop
      const rowTop = activeLegendRow.offsetTop
      const rowBottom = rowTop + activeLegendRow.offsetHeight
      const safetyTop = currentScrollTop + LEGEND_ACTIVE_ROW_EDGE_SAFETY_PX
      const safetyBottom =
        currentScrollTop + viewportHeight - LEGEND_ACTIVE_ROW_EDGE_SAFETY_PX

      let targetScrollTop = currentScrollTop
      if (rowTop < safetyTop) {
        targetScrollTop = rowTop - LEGEND_ACTIVE_ROW_EDGE_SAFETY_PX
      } else if (rowBottom > safetyBottom) {
        targetScrollTop =
          rowBottom - viewportHeight + LEGEND_ACTIVE_ROW_EDGE_SAFETY_PX
      }

      const nextScrollTop = Math.min(Math.max(targetScrollTop, 0), maxScrollTop)

      if (Math.abs(nextScrollTop - currentScrollTop) < 0.5) {
        syncLegendOverflowState()
        return
      }

      viewport.scrollTop = nextScrollTop
      syncLegendOverflowState()
    }

    const queueMeasure = () => {
      window.cancelAnimationFrame(animationFrame)
      animationFrame = window.requestAnimationFrame(measureAndAdjust)
    }

    queueMeasure()

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            queueMeasure()
          })

    resizeObserver?.observe(viewport)
    resizeObserver?.observe(legendList)
    resizeObserver?.observe(activeLegendRow)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      resizeObserver?.disconnect()
    }
  }, [activeBucketKey, slices, syncLegendOverflowState])

  return (
    <div className="flex h-full max-h-full min-h-0 min-w-0 self-stretch overflow-hidden p-1">
      <div className="relative h-full min-h-0 w-full">
        <div
          ref={viewportRef}
          className="flex h-full w-full min-h-0 flex-col overflow-x-hidden overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="uploaded-conversations-legend-reel-viewport"
          onMouseEnter={() => {
            isPointerOverLegendRef.current = true
          }}
          onMouseLeave={() => {
            isPointerOverLegendRef.current = false
          }}
        >
          <ul
            ref={legendListRef}
            className={cn(
              "w-full space-y-1.5",
              legendOverflowState.hasOverflow ? "my-0" : "my-auto",
            )}
            style={{
              paddingBlock: LEGEND_ACTIVE_ROW_EDGE_SAFETY_PX,
            }}
            data-testid="uploaded-conversations-legend-reel"
          >
            {slices.map((slice, sliceIndex) => {
              const isActive = slice.bucket.key === activeBucketKey
              const markerColor =
                sliceMarkPalette[sliceIndex] ?? "var(--coval-chart-series-1)"

              return (
                <li
                  key={slice.bucket.key}
                  ref={(rowElement) => {
                    legendRowRefs.current[slice.bucket.key] = rowElement
                  }}
                  onMouseEnter={() => {
                    onBucketHoverChange?.(slice.bucket.key)
                  }}
                  onMouseLeave={() => {
                    onBucketHoverChange?.(null)
                  }}
                >
                  <button
                    type="button"
                    className="flex w-full items-start gap-2 px-0.5 py-1 text-left focus-visible:outline-none"
                    onBlur={() => {
                      onBucketHoverChange?.(null)
                    }}
                    onClick={() => {
                      onBucketClick?.(slice.bucket.key)
                    }}
                    onFocus={() => {
                      onBucketHoverChange?.(slice.bucket.key)
                    }}
                    data-testid={`uploaded-conversations-legend-row-${slice.bucket.key}`}
                  >
                    <span
                      aria-hidden="true"
                      className="mt-1 block size-2 shrink-0 rounded-full ring-1 ring-inset ring-coval-border"
                      style={{ backgroundColor: markerColor }}
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          "block text-sm leading-5 transition-[color,transform,font-weight] duration-200 ease-out motion-reduce:transition-none",
                          isActive
                            ? "scale-[1.01] font-medium text-coval-text"
                            : "font-normal text-coval-text-muted",
                        )}
                        data-testid={`uploaded-conversations-legend-title-${slice.bucket.key}`}
                      >
                        {slice.bucket.label}
                      </span>
                      <span
                        className="mt-1 block"
                        data-testid={`uploaded-conversations-legend-description-${slice.bucket.key}`}
                      >
                        <span className="block text-xs leading-4 text-coval-text-muted">
                          {slice.bucket.description}
                        </span>
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>

        <div
          className={cn(
            "pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-gradient-to-b from-surface-contrast to-transparent transition-opacity duration-200",
            legendOverflowState.hasOverflow && legendOverflowState.canScrollUp
              ? "opacity-100"
              : "opacity-0",
          )}
          data-testid="uploaded-conversations-legend-reel-fade-top"
        />
        <div
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-gradient-to-t from-surface-contrast to-transparent transition-opacity duration-200",
            legendOverflowState.hasOverflow && legendOverflowState.canScrollDown
              ? "opacity-100"
              : "opacity-0",
          )}
          data-testid="uploaded-conversations-legend-reel-fade-bottom"
        />
      </div>
    </div>
  )
}
